import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ProductFilters, ActiveFilters, FilterOptions } from '../components/ProductFilters';
import { StarIcon, HeartIcon, EyeIcon, SearchXIcon, AlertCircleIcon, PlayIcon, PackageIcon } from 'lucide-react';
import { useProducts } from '../hooks/useFirestore';
import { FirestoreProduct } from '../firebase/firestore';
import { useFavorites } from '../hooks/useFavorites';
import { useAuth } from '../components/auth/AuthProvider';
// Product type definition
interface Product {
  id: number | string;
  title: string;
  description: string;
  price: number;
  category: string;
  subcategory?: string;
  image: string;
  rating: number;
  seller: string;
  platform?: string;
  licenseType?: string;
  useCase?: string;
  isFree?: boolean;
  format?: string;
  skillLevel?: string;
  industry?: string;
  creatorType?: string;
  isTrending?: boolean;
  isNewArrival?: boolean;
  dateAdded?: string;
  isVideo?: boolean;
  videoMetadata?: {
    width?: number;
    height?: number;
    isVertical?: boolean;
    duration?: number;
  };
}
// Map Firestore product to UI product
const mapFirestoreToProduct = (product: FirestoreProduct): Product => {
  return {
    id: product.id || '',
    title: product.title,
    description: product.description,
    price: product.price,
    category: product.category || 'Other',
    subcategory: product.subcategory,
    image: product.image || 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
    rating: product.rating || 4.5,
    seller: product.sellerName || 'Unknown Seller',
    platform: product.platform,
    licenseType: product.licenseType,
    useCase: product.useCase,
    isFree: product.price === 0 || product.isFree,
    format: product.format,
    skillLevel: product.skillLevel,
    industry: product.industry,
    creatorType: product.creatorType,
    isTrending: product.isTrending,
    isNewArrival: product.isNewArrival,
    isVideo: product.isVideo || product.category === 'Short-form Video',
    videoMetadata: product.videoMetadata
  };
};
// Skeleton loader for product cards
const ProductCardSkeleton = () => <div className="group relative bg-white dark:bg-[#162825] rounded-xl overflow-hidden shadow-sm flex flex-col h-full" data-id="element-4107">
    <div className="relative aspect-[4/3] overflow-hidden bg-gray-200 dark:bg-[#1A3330] animate-pulse" data-id="element-4108"></div>
    <div className="p-4 flex flex-col flex-1" data-id="element-4109">
      <div className="flex items-center justify-between mb-2" data-id="element-4110">
        <div className="flex items-center" data-id="element-4111">
          <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-[#1A3330] mr-2" data-id="element-4112"></div>
          <div className="h-3 w-16 bg-gray-200 dark:bg-[#1A3330] rounded" data-id="element-4113"></div>
        </div>
        <div className="flex items-center" data-id="element-4114">
          <div className="h-3.5 w-10 bg-gray-200 dark:bg-[#1A3330] rounded" data-id="element-4115"></div>
        </div>
      </div>
      <div className="h-5 w-3/4 bg-gray-200 dark:bg-[#1A3330] rounded mb-1" data-id="element-4116"></div>
      <div className="h-3 w-full bg-gray-200 dark:bg-[#1A3330] rounded mb-1" data-id="element-4117"></div>
      <div className="h-3 w-2/3 bg-gray-200 dark:bg-[#1A3330] rounded mb-2.5" data-id="element-4118"></div>
      <div className="flex gap-1.5 mb-2.5" data-id="element-4119">
        <div className="h-5 w-14 bg-gray-200 dark:bg-[#1A3330] rounded-full" data-id="element-4120"></div>
        <div className="h-5 w-12 bg-gray-200 dark:bg-[#1A3330] rounded-full" data-id="element-4121"></div>
      </div>
      <div className="flex items-center justify-between mt-auto" data-id="element-4122">
        <div className="h-6 w-16 bg-gray-200 dark:bg-[#1A3330] rounded" data-id="element-4123"></div>
        <div className="h-8 w-28 bg-gray-200 dark:bg-[#1A3330] rounded-full" data-id="element-4124"></div>
      </div>
    </div>
  </div>;
// Product card component with enhanced hover effect
const ProductCard: React.FC<{
  product: Product;
  isFavorited?: boolean;
  onToggleFavorite?: (productId: string) => void;
}> = ({
  product,
  isFavorited = false,
  onToggleFavorite
}) => {
  const navigate = useNavigate();
  const handlePreview = () => {
    navigate(`/product/${product.id}`);
  };
  const handleBuyNow = () => {
    navigate(`/product/${product.id}`);
  };
  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onToggleFavorite) {
      onToggleFavorite(String(product.id));
    }
  };
  // Determine if this is a video product
  const isVideo = product.isVideo || product.category === 'Short-form Video';
  const isVerticalVideo = product.videoMetadata?.isVertical || false;
  return <div className="group relative bg-white dark:bg-[#162825] rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col h-full" data-id="element-4125">
      {/* Thumbnail — fixed aspect ratio for all card types */}
      <div className="relative aspect-[4/3] overflow-hidden cursor-pointer bg-black" onClick={handlePreview} data-id="element-4126">
        <img src={product.image} alt={product.title} className="w-full h-full object-cover transition-transform duration-400 ease-in-out group-hover:scale-105" onError={e => {
        e.currentTarget.src = 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80';
      }} data-id="element-4127" />
        {/* Video play indicator */}
        {isVideo && <div className="absolute inset-0 flex items-center justify-center" data-id="element-4128">
            <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center" data-id="element-4129">
              <PlayIcon className="h-6 w-6 text-white" data-id="element-4130" />
            </div>
          </div>}
        <button onClick={handleFavoriteClick} className={`absolute top-3 right-3 bg-white dark:bg-[#162825] rounded-full p-2 shadow-md transition-all duration-200 ${isFavorited ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'} data-id="element-4131">
          <HeartIcon className={`h-5 w-5 cursor-pointer transition-colors duration-200 ${isFavorited ? 'text-red-500 fill-red-500' : 'text-gray-500 dark:text-[#7A8F8A] hover:text-red-500 dark:hover:text-red-400'}`} data-id="element-4132" />
        </button>
        {/* Category tag */}
        <div className="absolute top-3 left-3" data-id="element-4133">
          <span className="bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full" data-id="element-4134">
            {product.category}
          </span>
        </div>
        {/* Video Type indicator for video products */}
        {isVideo && <div className="absolute bottom-3 left-3" data-id="element-4135">
            <span className="bg-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full" data-id="element-4136">
              {isVerticalVideo ? 'Vertical' : 'Horizontal'} Video
            </span>
          </div>}
        {/* Free badge */}
        {product.isFree && !isVideo && <div className="absolute bottom-3 left-3" data-id="element-4137">
            <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full" data-id="element-4138">
              FREE
            </span>
          </div>}
        {/* Trending badge */}
        {product.isTrending && <div className="absolute bottom-3 right-3" data-id="element-4139">
            <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center" data-id="element-4140">
              <span className="mr-1" data-id="element-4141">🔥</span>Trending
            </span>
          </div>}
        {/* New badge */}
        {product.isNewArrival && !product.isTrending && <div className="absolute bottom-3 right-3" data-id="element-4142">
            <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center" data-id="element-4143">
              <span className="mr-1" data-id="element-4144">✨</span>New
            </span>
          </div>}
      </div>
      {/* Content section — flex-1 pushes price row to bottom */}
      <div className="p-4 flex flex-col flex-1" data-id="element-4145">
        <div className="flex items-center justify-between mb-1.5" data-id="element-4146">
          {/* Seller info */}
          <div className="flex items-center" data-id="element-4147">
            <div className="w-5 h-5 rounded-full bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] flex items-center justify-center text-white text-xs mr-2 flex-shrink-0" data-id="element-4148">
              {product.seller.charAt(0)}
            </div>
            <span className="text-xs text-gray-600 dark:text-[#7A8F8A] truncate" data-id="element-4149">
              by {product.seller}
            </span>
          </div>
          {/* Rating */}
          <div className="flex items-center flex-shrink-0" data-id="element-4150">
            <StarIcon className="h-3.5 w-3.5 text-yellow-400 fill-current" data-id="element-4151" />
            <span className="text-xs text-gray-600 dark:text-[#7A8F8A] ml-1" data-id="element-4152">
              {product.rating}
            </span>
          </div>
        </div>
        <h3 className="text-base font-semibold mb-1 text-gray-800 dark:text-[#F8FAF9] cursor-pointer line-clamp-1" onClick={handlePreview} data-id="element-4153">
          {product.title}
        </h3>
        <p className="text-xs text-gray-500 dark:text-[#7A8F8A] mb-2.5 line-clamp-2 min-h-[2rem]" data-id="element-4154">
          {product.description}
        </p>
        {/* Tags — fixed height to prevent layout shift */}
        <div className="flex flex-wrap gap-1.5 mb-2.5 min-h-[24px]" data-id="element-4155">
          {product.platform && <span className="text-xs bg-gray-100 dark:bg-[#1A3330] text-gray-600 dark:text-[#B0C1BD] px-1.5 py-0.5 rounded-full" data-id="element-4156">
              {product.platform}
            </span>}
          {product.format && <span className="text-xs bg-gray-100 dark:bg-[#1A3330] text-gray-600 dark:text-[#B0C1BD] px-1.5 py-0.5 rounded-full" data-id="element-4157">
              {product.format}
            </span>}
          {product.skillLevel && <span className="text-xs bg-gray-100 dark:bg-[#1A3330] text-gray-600 dark:text-[#B0C1BD] px-1.5 py-0.5 rounded-full" data-id="element-4158">
              {product.skillLevel}
            </span>}
        </div>
        {/* Price + buttons — always at bottom */}
        <div className="flex items-center justify-between mt-auto" data-id="element-4159">
          <span className="text-base font-bold text-gray-900 dark:text-white" data-id="element-4160">
            {product.isFree ? 'Free' : `$${product.price}`}
          </span>
          <div className="flex space-x-1" data-id="element-4161">
            <button className="bg-[#0A3C52] dark:bg-[#0B3C45] text-white px-2.5 py-1.5 rounded-l-full text-xs font-medium flex items-center justify-center hover:bg-[#0D4B65] transition-colors" onClick={handlePreview} data-id="element-4162">
              <EyeIcon className="h-3 w-3 mr-1" data-id="element-4163" />
              Preview
            </button>
            <button className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white px-2.5 py-1.5 rounded-r-full text-xs font-medium hover:bg-[#25A077] transition-colors" onClick={handleBuyNow} data-id="element-4164">
              Buy Now
            </button>
          </div>
        </div>
      </div>
    </div>;
};
// Empty state component for no matching products
const EmptyState = ({
  clearAllFilters
}: {
  clearAllFilters: () => void;
}) => <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center" data-id="element-4165">
    <div className="w-16 h-16 bg-gray-100 dark:bg-[#1A3330] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4166">
      <SearchXIcon className="h-8 w-8 text-gray-400 dark:text-[#7A8F8A]" data-id="element-4167" />
    </div>
    <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4168">
      No matching products found
    </h3>
    <p className="text-gray-500 dark:text-[#7A8F8A] mb-6" data-id="element-4169">
      Try adjusting your filters or search term
    </p>
    <button onClick={clearAllFilters} className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-[#1A3330] rounded-lg text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-200 dark:hover:bg-[#0B2C2A] transition-colors" data-id="element-4170">
      Clear all filters
    </button>
  </div>;
// Empty marketplace state — no products published yet
const EmptyMarketplace = () => <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center" data-id="element-4171">
    <div className="w-16 h-16 bg-gray-100 dark:bg-[#1A3330] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4172">
      <PackageIcon className="h-8 w-8 text-gray-400 dark:text-[#7A8F8A]" data-id="element-4173" />
    </div>
    <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4174">
      No products available yet
    </h3>
    <p className="text-gray-500 dark:text-[#7A8F8A] mb-6" data-id="element-4175">
      Products will appear here once creators publish them.
    </p>
  </div>;
// Error state component — actual loading failures
const ErrorState = ({
  error,
  onRetry
}: {
  error: Error;
  onRetry: () => void;
}) => <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center" data-id="element-4176">
    <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4177">
      <AlertCircleIcon className="h-8 w-8 text-red-500 dark:text-red-400" data-id="element-4178" />
    </div>
    <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4179">
      Error loading products
    </h3>
    <p className="text-gray-500 dark:text-[#7A8F8A] mb-6" data-id="element-4180">
      {error.message || 'Something went wrong. Please try again.'}
    </p>
    <button onClick={onRetry} className="inline-flex items-center px-4 py-2 bg-[#2DB489] dark:bg-[#4ED1A0] rounded-lg text-white hover:bg-[#25A077] dark:hover:bg-[#3DC79D] transition-colors" data-id="element-4181">
      Try Again
    </button>
  </div>;
export const BrowseProductsPage: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);
  const isUpdatingFromUrl = useRef(false);
  const {
    isFavorited,
    toggleFavorite
  } = useFavorites();
  const {
    user
  } = useAuth();
  // Filter options
  const filterOptions: FilterOptions = {
    categories: ['Website Templates', 'Mobile App Kits', 'UI Kits', 'AI Prompt Packs', 'Component Libraries', 'Design Systems', 'Animations', 'Audio Packs', 'No-Code MVPs', 'Notion Templates', 'eBooks & PDF Guides', 'Course Slides & Templates', 'Printable Planners', 'Email Templates & Flows', 'Copywriting Packs', 'Fonts & Typefaces', 'Icon Packs', '3D Assets & Mockups', 'Canva Templates', 'Social Media Content Packs', 'Legal Templates', 'Resume & Portfolio Templates', 'Short-form Video', 'Other'],
    platforms: ['React', 'HTML', 'Figma', 'Webflow', 'Photoshop', 'Illustrator', 'Notion', 'WordPress', 'Shopify', 'Canva', 'Framer', 'PowerPoint / Google Slides', 'ChatGPT / OpenAI', 'Blender', 'TikTok', 'Instagram', 'YouTube'],
    formats: ['PDF', 'ZIP', 'PNG', 'JPG', 'MP3', 'MP4', 'PSD', '.fig', '.blend', 'SVG'],
    skillLevels: ['Beginner', 'Intermediate', 'Advanced'],
    industries: ['Startups / SaaS', 'Education', 'Wellness & Health', 'Finance', 'Music / Audio', 'Real Estate', 'AI & Tech', 'Creators / Influencers'],
    creatorTypes: ['Designer', 'Developer', 'Marketer', 'Content Creator', 'Entrepreneur', 'Music Producer'],
    priceRanges: ['Free', 'Under $20', '$20 - $50', 'Over $50'],
    licenseTypes: ['Personal', 'Commercial', 'Extended'],
    useCases: ['SaaS', 'Portfolio', 'Mobile App', 'YouTube', 'Education'],
    subcategories: {
      'Website Templates': [],
      'Mobile App Kits': [],
      'UI Kits': [],
      'AI Prompt Packs': [],
      'Component Libraries': [],
      'Design Systems': [],
      Animations: [],
      'Audio Packs': [],
      'No-Code MVPs': [],
      'Notion Templates': [],
      'eBooks & PDF Guides': [],
      'Course Slides & Templates': [],
      'Printable Planners': [],
      'Email Templates & Flows': [],
      'Copywriting Packs': [],
      'Fonts & Typefaces': [],
      'Icon Packs': [],
      '3D Assets & Mockups': [],
      'Canva Templates': [],
      'Social Media Content Packs': [],
      'Legal Templates': [],
      'Resume & Portfolio Templates': [],
      'Short-form Video': ['Motivational & Inspirational', 'Business & Finance', 'Fitness & Health', 'Lifestyle & Travel', 'Entertainment & Memes', 'Gaming Clips', 'Educational & How-To', 'Product Reviews & Unboxings', 'Food & Cooking', 'Customizable Templates'],
      Other: []
    }
  };
  // Active filters state
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({
    categories: [],
    platforms: [],
    priceRanges: [],
    licenseTypes: [],
    useCases: [],
    formats: [],
    skillLevels: [],
    industries: [],
    creatorTypes: [],
    search: '',
    trending: false,
    newArrivals: false,
    subcategories: [] // Ensure subcategories is always an array
  });
  // Convert active filters to Firestore filters
  const convertToFirestoreFilters = useCallback((filters: ActiveFilters) => {
    const firestoreFilters: Record<string, any> = {};
    if (filters.categories.length === 1) {
      firestoreFilters.category = filters.categories[0];
    }
    if (filters.trending) {
      firestoreFilters.trending = true;
    }
    if (filters.newArrivals) {
      firestoreFilters.newArrivals = true;
    }
    // Note: More complex filters will be handled client-side for now
    // as Firestore has limitations on compound queries
    return firestoreFilters;
  }, []);
  // Use the products hook
  const {
    products: firestoreProducts,
    loading,
    initialLoading,
    error,
    hasMore,
    loadMore,
    updateFilters
  } = useProducts(12, convertToFirestoreFilters(activeFilters), user?.id);
  // Convert Firestore products to UI products
  const products = firestoreProducts.map(mapFirestoreToProduct);
  // Apply client-side filtering for complex filters
  const filteredProducts = products.filter(product => {
    // Skip client-side filtering only if NO client-side filters are active
    if (activeFilters.categories.length <= 1 && !activeFilters.trending && !activeFilters.newArrivals && activeFilters.subcategories.length === 0 && activeFilters.platforms.length === 0 && activeFilters.formats.length === 0 && activeFilters.skillLevels.length === 0 && activeFilters.industries.length === 0 && activeFilters.creatorTypes.length === 0 && activeFilters.priceRanges.length === 0) {
      return true;
    }
    // Apply category filter (if more than one category selected)
    if (activeFilters.categories.length > 1 && !activeFilters.categories.includes(product.category)) {
      return false;
    }
    // Apply subcategory filter
    if (activeFilters.subcategories.length > 0 && (!product.subcategory || !activeFilters.subcategories.includes(product.subcategory))) {
      return false;
    }
    // Apply platform filter
    if (activeFilters.platforms.length > 0 && (!product.platform || !activeFilters.platforms.includes(product.platform))) {
      return false;
    }
    // Apply format filter
    if (activeFilters.formats.length > 0 && (!product.format || !activeFilters.formats.includes(product.format))) {
      return false;
    }
    // Apply skill level filter
    if (activeFilters.skillLevels.length > 0 && (!product.skillLevel || !activeFilters.skillLevels.includes(product.skillLevel))) {
      return false;
    }
    // Apply industry filter
    if (activeFilters.industries.length > 0 && (!product.industry || !activeFilters.industries.includes(product.industry))) {
      return false;
    }
    // Apply creator type filter
    if (activeFilters.creatorTypes.length > 0 && (!product.creatorType || !activeFilters.creatorTypes.includes(product.creatorType))) {
      return false;
    }
    // Apply price range filter
    if (activeFilters.priceRanges.length > 0) {
      let matchesPrice = false;
      if (activeFilters.priceRanges.includes('Free') && product.isFree) {
        matchesPrice = true;
      }
      if (activeFilters.priceRanges.includes('Under $20') && product.price > 0 && product.price < 20) {
        matchesPrice = true;
      }
      if (activeFilters.priceRanges.includes('$20 - $50') && product.price >= 20 && product.price <= 50) {
        matchesPrice = true;
      }
      if (activeFilters.priceRanges.includes('Over $50') && product.price > 50) {
        matchesPrice = true;
      }
      if (!matchesPrice) return false;
    }
    // Apply license type filter
    if (activeFilters.licenseTypes.length > 0 && (!product.licenseType || !activeFilters.licenseTypes.includes(product.licenseType))) {
      return false;
    }
    // Apply use case filter
    if (activeFilters.useCases.length > 0 && (!product.useCase || !activeFilters.useCases.includes(product.useCase))) {
      return false;
    }
    // Apply search filter
    if (activeFilters.search) {
      const searchTerm = activeFilters.search.toLowerCase();
      if (!product.title.toLowerCase().includes(searchTerm) && !product.description.toLowerCase().includes(searchTerm) && !product.seller.toLowerCase().includes(searchTerm)) {
        return false;
      }
    }
    return true;
  });
  // Reset scroll position when location changes
  useEffect(() => {
    window.scrollTo(0, 0);
    // Parse URL parameters
    const searchParams = new URLSearchParams(location.search);
    const newFilters: ActiveFilters = {
      categories: [],
      platforms: [],
      priceRanges: [],
      licenseTypes: [],
      useCases: [],
      formats: [],
      skillLevels: [],
      industries: [],
      creatorTypes: [],
      search: searchParams.get('search') || '',
      trending: false,
      newArrivals: false,
      subcategories: [] // Ensure subcategories is always an array
    };
    // Get category from URL if present
    const category = searchParams.get('category');
    if (category) {
      newFilters.categories = [category];
    }
    // Prevent the URL-update effect from re-triggering this effect
    isUpdatingFromUrl.current = true;
    // Set active filters from URL
    setActiveFilters(newFilters);
    // Update Firestore filters
    updateFilters(convertToFirestoreFilters(newFilters));
  }, [location.pathname, location.search, convertToFirestoreFilters, updateFilters]);
  // Handle scroll for navbar
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);
  // Handle filter changes
  const handleFilterChange = (newFilters: ActiveFilters) => {
    setActiveFilters(newFilters);
    updateFilters(convertToFirestoreFilters(newFilters));
  };
  // Clear all filters
  const clearAllFilters = useCallback(() => {
    const clearedFilters = {
      categories: [],
      platforms: [],
      priceRanges: [],
      licenseTypes: [],
      useCases: [],
      formats: [],
      skillLevels: [],
      industries: [],
      creatorTypes: [],
      search: '',
      trending: false,
      newArrivals: false,
      subcategories: []
    };
    setActiveFilters(clearedFilters);
    updateFilters(convertToFirestoreFilters(clearedFilters));
  }, [updateFilters, convertToFirestoreFilters]);
  // Handle retry on error
  const handleRetry = () => {
    updateFilters(convertToFirestoreFilters(activeFilters));
  };
  // Update URL when filters change (skip if filters were just set from URL)
  useEffect(() => {
    if (isUpdatingFromUrl.current) {
      isUpdatingFromUrl.current = false;
      return;
    }
    const searchParams = new URLSearchParams();
    if (activeFilters.search) {
      searchParams.set('search', activeFilters.search);
    }
    if (activeFilters.categories.length === 1) {
      searchParams.set('category', activeFilters.categories[0]);
    }
    const queryString = searchParams.toString();
    navigate({
      pathname: location.pathname,
      search: queryString ? `?${queryString}` : ''
    }, {
      replace: true
    });
  }, [activeFilters, navigate, location.pathname]);
  // Render skeleton loaders during initial loading
  const renderSkeletons = () => {
    return Array(6).fill(0).map((_, index) => <ProductCardSkeleton key={`skeleton-${index}`} data-id="element-4182" />);
  };
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" ref={pageRef} data-id="element-4183">
      <Navbar scrolled={scrolled} data-id="element-4184" />
      <main className="pt-24 pb-20" data-id="element-4185">
        <div className="container mx-auto px-4 md:px-6" data-id="element-4186">
          <div className="text-center mb-12" data-id="element-4187">
            <h1 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-transparent bg-clip-text" data-id="element-4188">
              Browse Digital Products
            </h1>
            <p className="text-xl text-gray-600 dark:text-[#B0C1BD] max-w-3xl mx-auto" data-id="element-4189">
              Discover premium digital assets created by our community of
              talented designers and developers
            </p>
          </div>
          <div className="flex flex-col md:flex-row gap-8" data-id="element-4190">
            {/* Filters sidebar */}
            <div className="w-full md:w-64 md:min-w-[16rem] flex-shrink-0" style={{
            containIntrinsicBlockSize: '800px',
            contentVisibility: 'visible'
          }} data-id="element-4191">
              <ProductFilters filterOptions={filterOptions} activeFilters={activeFilters} onFilterChange={handleFilterChange} totalProducts={products.length} filteredCount={filteredProducts.length} data-id="element-4192" />
            </div>
            {/* Product grid with fixed height to prevent layout shifts */}
            <div className="flex-grow product-grid-container" style={{
            minHeight: '100vh'
          }} data-id="element-4193">
              {initialLoading ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-4194">
                  {renderSkeletons()}
                </div> : error && error.message && (error.message.toLowerCase().includes('permission') || error.message.toLowerCase().includes('insufficient')) ? <EmptyMarketplace data-id="element-4195" /> : error ? <ErrorState error={error} onRetry={handleRetry} data-id="element-4196" /> : filteredProducts.length > 0 ? <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-4197">
                    {filteredProducts.map(product => <ProductCard key={product.id} product={product} isFavorited={isFavorited(String(product.id))} onToggleFavorite={toggleFavorite} data-id="element-4198" />)}
                  </div>
                  {/* Load more button */}
                  {hasMore && <div className="text-center mt-12" data-id="element-4199">
                      <button className="bg-white dark:bg-[#162825] border border-gray-200 dark:border-[#1A3330] text-gray-800 dark:text-[#F8FAF9] px-8 py-3 rounded-full text-lg font-medium hover:border-[#2DB489] hover:text-[#2DB489] dark:hover:border-[#4ED1A0] dark:hover:text-[#4ED1A0] disabled:opacity-50 disabled:cursor-not-allowed" onClick={loadMore} disabled={loading} data-id="element-4200">
                        {loading ? <span className="flex items-center justify-center" data-id="element-4201">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#2DB489] dark:text-[#4ED1A0]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-4202">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-4203"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-4204"></path>
                            </svg>
                            Loading...
                          </span> : 'Load More Products'}
                      </button>
                    </div>}
                </> : <EmptyState clearAllFilters={clearAllFilters} data-id="element-4205" />}
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-4206" />
    </div>;
};